// v6.33.0 — Sentry must be imported FIRST so its global handlers
// register before any other React or library code initializes.
// If REACT_APP_SENTRY_DSN is unset, this module is a no-op.
import Sentry from "@/sentry";

import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

// v6.33.0 — Top-level ErrorBoundary catches React render-cycle errors
// that the global window.onerror handler can't. Renders a friendly
// shadcn Alert fallback so a single component crash doesn't blank the
// entire app. Both Sentry-init and non-Sentry-init paths use this —
// when Sentry is disabled, errors still go to console + the fallback
// still renders gracefully (Sentry.ErrorBoundary degrades cleanly
// when SDK is uninitialized).
function AppErrorFallback({ error, resetError }) {
  return (
    <div
      data-testid="app-error-boundary-fallback"
      className="min-h-screen flex items-center justify-center bg-muted p-6"
    >
      <Alert variant="destructive" className="max-w-lg">
        <AlertTitle>Something went wrong</AlertTitle>
        <AlertDescription>
          An unexpected error occurred. Our team has been notified.{" "}
          <button
            type="button"
            className="underline hover:no-underline"
            data-testid="app-error-boundary-retry"
            onClick={resetError}
          >
            Try again
          </button>
        </AlertDescription>
      </Alert>
    </div>
  );
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary
      fallback={AppErrorFallback}
      beforeCapture={(scope) => {
        scope.setTag("section", "app-root");
      }}
    >
      <App />
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);
